import React, { useRef, useState, useEffect } from 'react';

import IconButton from '@mui/material/IconButton';

import Box from '@mui/material/Box';

import { colours } from 'src/constants/colours';

import Lightbox from 'src/components/ui/Lightbox';
import Icon from 'src/components/ui/Icon';
import Image from 'src/components/ui/Image';

import useModalOpenState from 'src/hooks/useModalOpenState';
import { styledComponent, styledTag } from 'src/utils/styled';

const StyledBox = styledComponent(Box)`
    display: flex;
    align-items: center;
    position: relative;
`;

const ChevronIconButton = styledComponent(IconButton)<{
    $side: 'left' | 'right';
}>`
    padding: 0.8rem;
    background-color: ${colours.white.O87};
    margin: 0.4rem;
    position: absolute;
    z-index: 1;
    ${({ $side }) => `${$side}:0.5rem;`};

    &:hover {
        background-color: ${colours.white.main};
    }
`;

const ScrollContainer = styledTag('div')`
    overflow-x: scroll; /* Allow horizontal scrolling */
    scroll-snap-type: x mandatory; /* Snap scrolling horizontally to available "snap points" */
    min-width: 100%;

    /* Hide horizontal scrollbar */
    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
`;

const Scroller = styledTag('div')<{
    $width: string;
}>`
    display: flex;
    flex-wrap: nowrap;
    min-width: 100%;
    width: ${({ $width }) => $width};
`;

const ImageContainer = styledTag('div')`
    border-radius: 0.375rem;
    overflow: hidden;
    height: 9.375rem;
    cursor: pointer;

    :not(:last-child) {
        margin-right: 0.625rem;
    }
`;

const cardWidthInPx = 150;
const marginRightBetweenCardInPx = 10;

interface Props {
    imagesUrls: string[];
}

const ImagesScroller: React.FC<Props> = ({ imagesUrls }) => {
    const scrollerContainerRef = useRef<HTMLDivElement | null>(null);
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const [isTheEnd, setIsTheEnd] = useState(false);
    const [isTheStart, setIsTheStart] = useState(true);
    const [showArrows, setShowArrows] = useState(false);
    const { props: lightboxProps, open: openLightBoxModal } = useModalOpenState();
    const [lightboxMainImageUrl, setLightboxMainImageUrl] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (imagesUrls.length && scrollerContainerRef.current) {
            if (
                scrollerContainerRef.current.clientWidth <
                imagesUrls.length * (cardWidthInPx + marginRightBetweenCardInPx)
            ) {
                setShowArrows(true);
            } else {
                setShowArrows(false);
            }
        }
    }, [imagesUrls]);

    const handleScroll = () => {
        const isTheEndTemp =
            Math.round(scrollerContainerRef.current?.scrollLeft ?? 0) >=
            Math.round(
                (scrollerContainerRef.current?.scrollWidth ?? 0) - (scrollerContainerRef.current?.clientWidth ?? 0),
            );
        if (isTheEndTemp || isTheEnd) {
            setIsTheEnd(isTheEndTemp);
        }
        const isTheStartTemp = Math.round(scrollerContainerRef.current?.scrollLeft ?? 0) === 0;
        if (isTheStartTemp || isTheStart) {
            setIsTheStart(isTheStartTemp);
        }
    };

    const handleNavigatePrev = () => {
        if (scrollerContainerRef.current && scrollerRef.current && imagesUrls.length) {
            const cardWidth = Math.floor(scrollerRef.current.clientWidth / imagesUrls.length);
            scrollerContainerRef.current.scrollTo({
                behavior: 'smooth',
                left: scrollerContainerRef.current.scrollLeft - cardWidth,
            });
        }
    };

    const handleNavigateNext = () => {
        if (scrollerContainerRef.current && scrollerRef.current && imagesUrls.length) {
            const cardWidth = Math.floor(scrollerRef.current.clientWidth / imagesUrls.length);
            scrollerContainerRef.current.scrollTo({
                behavior: 'smooth',
                left: scrollerContainerRef.current.scrollLeft + cardWidth,
            });
        }
    };

    const scrollerWidthInPx =
        (cardWidthInPx + marginRightBetweenCardInPx) * imagesUrls.length - marginRightBetweenCardInPx;

    return (
        <StyledBox>
            {showArrows && !isTheStart && (
                <ChevronIconButton $side="left" onClick={handleNavigatePrev}>
                    <Icon size="0.6rem" name="ChevronLeft" />
                </ChevronIconButton>
            )}

            <ScrollContainer ref={scrollerContainerRef} onScroll={handleScroll}>
                <Scroller ref={scrollerRef} $width={`${scrollerWidthInPx}px`}>
                    {imagesUrls.map((imageUrl) => (
                        <ImageContainer key={imageUrl}>
                            <Image
                                data-target="images-scroller-image"
                                data-target-id={`images-scroller-image-${imageUrl}`}
                                alt="images-scroller-image"
                                src={imageUrl}
                                width={cardWidthInPx}
                                height={cardWidthInPx}
                                onClick={() => {
                                    setLightboxMainImageUrl(imageUrl);
                                    openLightBoxModal();
                                }}
                                style={{ objectFit: 'cover' }}
                            />
                        </ImageContainer>
                    ))}
                    {lightboxProps.open && (
                        <Lightbox
                            {...lightboxProps}
                            imagesUrls={imagesUrls}
                            mainImageUrl={lightboxMainImageUrl}
                            enableZoom
                        />
                    )}
                </Scroller>
            </ScrollContainer>
            {showArrows && !isTheEnd && (
                <ChevronIconButton $side="right" onClick={handleNavigateNext}>
                    <Icon size="0.6rem" name="ChevronRight" />
                </ChevronIconButton>
            )}
        </StyledBox>
    );
};

export default ImagesScroller;
